import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Link,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useList } from "src/hooks/useFetch.hook";

// Shows a charging site's chargers with their connector power (kW), and links
// each charger to its details page. The site stores only charger IDs, so this
// joins them against the team's enriched charger list (connectors/status) to
// surface kW and the chargepoint_pk needed for the details link.

const normalizeId = (entry) =>
  (typeof entry === "string"
    ? entry
    : entry?.chargepoint_id || entry?.id || entry?.chargerId || entry?.name) ||
  "";

const connectorPowers = (charger) => {
  const connectors = Array.isArray(charger?.connectors) ? charger.connectors : [];
  return connectors.map((c, i) => {
    const kw = parseFloat(c?.kw);
    return {
      label: `Connector ${i + 1}`,
      power: !isNaN(kw) && kw > 0 ? `${kw} kW` : "—",
    };
  });
};

const STATUS_COLOR = { Active: "#2e7d32" };

const SiteChargersList = ({ chargers, onRemove, isReadOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;

  // Enriched team charger list (same source as the dashboard panel).
  const { data: enriched, isFetching } = useList("chargers", undefined, undefined, {
    limit: 500,
    enrich: true,
  });

  const byId = useMemo(() => {
    const map = new Map();
    (Array.isArray(enriched) ? enriched : []).forEach((c) => {
      const id = c?.chargepoint_id || c?.charge_box_id;
      if (id) map.set(String(id), c);
    });
    return map;
  }, [enriched]);

  const rows = useMemo(
    () =>
      (Array.isArray(chargers) ? chargers : []).map((entry, index) => {
        const id = String(normalizeId(entry));
        return { id, index, charger: byId.get(id) || null };
      }),
    [chargers, byId]
  );

  if (rows.length === 0) {
    return (
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        No chargers attached to this site.
      </Typography>
    );
  }

  const openCharger = (pk) => navigate(`/charger/${pk}`, { state: { returnTo } });

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Attached Chargers ({rows.length}):
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="site chargers">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Charger ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Connector Power</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              {!isReadOnly && onRemove && <TableCell sx={{ width: 48 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ id, index, charger }) => {
              const pk = charger?.chargepoint_pk;
              const powers = connectorPowers(charger);
              return (
                <TableRow key={`${id}-${index}`} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {pk != null ? (
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() => openCharger(pk)}
                        sx={{ fontWeight: 600, textAlign: "left", cursor: "pointer" }}
                      >
                        {id}
                      </Link>
                    ) : (
                      id
                    )}
                  </TableCell>
                  <TableCell>
                    {powers.length > 0 ? (
                      <Stack spacing={0.25}>
                        {powers.map((c, i) => (
                          <Typography key={i} variant="caption" sx={{ whiteSpace: "nowrap" }}>
                            {c.label}: <strong>{c.power}</strong>
                          </Typography>
                        ))}
                      </Stack>
                    ) : isFetching ? (
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        Loading…
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {charger?.status ? (
                      <Chip
                        size="small"
                        label={charger.status === "Active" ? "Online" : "Offline"}
                        sx={{
                          fontWeight: 600,
                          color: STATUS_COLOR[charger.status] || "#d32f2f",
                          bgcolor: `${STATUS_COLOR[charger.status] || "#d32f2f"}18`,
                        }}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  {!isReadOnly && onRemove && (
                    <TableCell sx={{ width: 48 }}>
                      <Tooltip title="Remove from site" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onRemove(index)}
                          aria-label={`remove charger ${id}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

SiteChargersList.propTypes = {
  chargers: PropTypes.array,
  onRemove: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

export default SiteChargersList;
